import { ESPaginationInfo, PostServiceStats } from 'server/api/types';
import { Post } from 'lib/types';
import { abbreviateNum } from 'lib/helpers';
import { compact, shuffle, uniqBy } from 'lodash';
import { ProfileLink } from 'components/ProfileLink';

export const getCreatorLink = (user: any, prefix?: string) => {
  if (!user) {
    return '';
  }

  return (
    <span className="underline" key={user.username}>
      <ProfileLink user={user} linkToExplore={user.isHidden}>
        {prefix}
        {user.name}
      </ProfileLink>
    </span>
  );
};

/**
 * Takes a number (or undefined) and returns a string with an order of magnitude
 * number of posts e.g. "millions of", "thousands of", "hundreds of".
 */
function getNumbersOfString(count?: number) {
  if (count === undefined || count > 1000000 || count === null) {
    return 'millions of';
  }
  if (count > 1000) {
    return 'thousands of';
  }
  if (count > 100) {
    return 'hundreds of';
  }
  return '';
}

export const exploreSearchSummary = (
  posts: Post[], meta?: ESPaginationInfo, statsMeta?: PostServiceStats, title?: string,
) => {
  try {
    if (posts.length < 3) {
      return null;
    }

    const firstString = `Discover ${getNumbersOfString(meta?.count)} ${title || 'posts on Gondola'}`;
    const randomPosts = shuffle(posts);

    const randomAccounts = () => {
      const accounts = compact(randomPosts.map(
        (p) => getCreatorLink({ username: p.account, name: p.account }, '@'),
      ));
      return shuffle([...uniqBy(accounts, 'key')].slice(0, 3));
    };
    const randomCompanies = randomAccounts();
    const accountNameString = (
      <>
        {' '}
        {randomCompanies[0]}
        ,
        {' '}
        {randomCompanies[1]}
        , and
        {' '}
        {randomCompanies[2]}
        ,
      </>
    );

    const randomCreditedUsers = () => {
      const userNames = compact(randomPosts.map((p) => {
        if (p.credits?.length) {
          return shuffle(p.credits.filter((c) => c.user?.userTypeId === 2))[0]?.user;
        }
        return null;
      }));
      return shuffle([...uniqBy(userNames, 'id')].slice(0, 3));
    };
    const secondString = 'These posts have credits from creators like ';

    const randomCreditedRoles = () => {
      const roleNames = compact(randomPosts.map((p) => {
        if (p.credits?.length) {
          return shuffle(p.credits.filter(
            (c) => c.user?.userTypeId === 2,
          ))?.[0]?.role?.name?.toLowerCase();
        }
        return null;
      }));
      return new Intl.ListFormat().format([...new Set(roleNames)].slice(0, 3));
    };
    const thirdString = `credited in roles like ${randomCreditedRoles()}.`;

    const statsString = () => {
      const s = 'these posts represent the diverse community of Gondola.';
      if ((statsMeta?.totalViews || 0 > 100) && (statsMeta?.totalLikes || 0) > 100) {
        return `With a combined ${abbreviateNum(statsMeta?.totalViews || 0, 0)} views and ${abbreviateNum(statsMeta?.totalLikes || 0, 0)} likes, ${s}`;
      }
      if ((statsMeta?.totalLikes || 0) > 100) {
        return `With ${abbreviateNum(statsMeta?.totalLikes || 0, 0)} likes, ${s}`;
      }
      return null;
    };

    const randomCreators = randomCreditedUsers();
    if (randomCreators.length < 3) {
      return (
        <h3 className="text-sm text-center">
          Discover
          {' '}
          {getNumbersOfString(meta?.count)}
          {' '}
          {title || 'posts'}
          {' '}
          on Gondola.
        </h3>
      );
    }
    const creatorNameString = (
      <>
        {' '}
        {getCreatorLink(randomCreators[0])}
        ,
        {' '}
        {getCreatorLink(randomCreators[1])}
        , and
        {' '}
        {getCreatorLink(randomCreators[2])}
        ,
      </>
    );

    return (
      <h3 className="text-sm text-center">
        {firstString}
        {' '}
        from accounts such as
        {' '}
        {accountNameString}
        .
        {' '}
        {secondString}
        {creatorNameString}
        {' '}
        {thirdString}
        {' '}
        {statsString()}
        {' '}
        Explore more posts and the creators behind them -- only on Gondola.
      </h3>
    );
  } catch (err) {
    return null;
  }
};
